import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RowDensity } from '../../../enum/RowDensity';
import { getWindowWidth, isMobileScreen } from '../../../util/mobile';
import { SidebarMenu } from '../../../enum/SidebarMenu';
import { ApiConsumer } from '../../../service/api-consumer';
import { AgmsResponseCode } from '../../../enum/AgmsResponseCode';
import {
  getConfigFromLocalStorage,
  getItemFromLocalStorage,
  setConfigToLocalStorage,
} from '../../../util/local-storage';
import { ApplicationConfig } from '../../../interface/response/ApplicationConfig';
import { AppDispatch } from '../../store';
import { updateApplicationConfig } from '../../../util/update-config';
import {
  CASH_REGISTER_PAGE,
  DEFAULT_TIMEZONE,
  DEFAULT_UNVISIBLE_COLUMNS,
  DEFAULT_VISIBLE_GEO_COLUMNS,
  EU,
  HIDDEN_COLUMNS,
  NUMBER_FORMAT_LOCALES,
  US,
} from '../../../constant/constants';
import { ThemeEnum } from '../../../enum/Theme';
import { applyTheme } from '../../../theme/applyTheme';
import { getThemeFromPlatform, PlatformType } from '../../../util/platform';
import { Column } from '../../../enum/Column';
import { isCurrencyColumn, isFilterDependentColumn, isGeoColumn, isUserColumn } from '../../../util/table-helper';
import { DeviceColumns } from '../../../enum/DeviceColumns';
import { PaymentTypeColumn } from '../../../enum/PaymentType';
import { GeoColumns } from '../../../enum/GeoDivisionColumnName';
import { StatsTableColumn } from '../../../enum/TableColumn';
import { moveItem } from '../../../util/move-item-helper';
import { StorageKey } from '../../../enum/StorageKey';

/*
 * ============================================================================
 * CONFIG SLICE
 * ============================================================================
 *
 * Centralized Redux slice responsible for storing and managing all
 * user-specific persistent UI/application configuration.
 *
 * PURPOSE
 * -------
 * This slice acts as the single source of truth for:
 *
 *   • UI preferences
 *   • Table configuration
 *   • User personalization
 *   • Layout state
 *   • Theme configuration
 *
 * The configuration is synchronized between:
 *
 *   1. Redux Store
 *   2. Backend User Config API
 *   3. LocalStorage fallback
 *
 * This guarantees consistent user experience across:
 *
 *   • Page refreshes
 *   • Browser sessions
 *   • Devices
 *   • Tabs/windows
 *
 *
 * ============================================================================
 * STORED SETTINGS
 * ============================================================================
 *
 * GENERAL UI
 * ----------
 *   • Row density
 *   • Theme
 *   • Time zone
 *   • Number format
 *   • Thumbnail mode
 *   • Sidebar state
 *   • Mobile/Desktop mode
 *   • Active sidebar menu
 *
 * TABLE CONFIGURATION
 * -------------------
 *   • Visible columns
 *   • Column order
 *   • Column visibility
 *   • Column pinning
 *   • Locked columns
 *   • Active page configuration
 *   • Table range information
 *
 * TABLE RANGE
 * -----------
 * Used for pagination/UI display information.
 *
 * Example:
 *   "1–25 of 240"
 *
 * Properties:
 *   • from  → first visible row index
 *   • to    → last visible row index
 *   • total → total dataset size
 *
 *
 * ============================================================================
 * DATA FLOW
 * ============================================================================
 *
 *                Backend API
 *                     │
 *                     │ getConfig()
 *                     ▼
 *              loadConfigFromBackend
 *                     │
 *                     ▼
 *               Redux configSlice
 *                     │
 *                     │ saveConfigToBackend()
 *                     ▼
 *                updateConfig()
 *                     │
 *                     ▼
 *                 LocalStorage
 *
 *
 * PRIORITY RULES
 * --------------
 * Backend configuration always has priority over LocalStorage.
 *
 * LocalStorage is used only:
 *   • as fallback when backend fails
 *   • for initial bootstrapping
 *   • for faster startup experience
 *
 *
 * ============================================================================
 * ASYNC ACTIONS
 * ============================================================================
 *
 * getConfig
 * ---------
 * Fetches full user configuration from backend API.
 *
 * Returns:
 *   Raw backend response object.
 *
 *
 * updateConfig
 * ------------
 * Sends updated ApplicationConfig object to backend API.
 *
 * Returns:
 *   Raw backend response object.
 *
 *
 * saveConfigToBackend
 * -------------------
 * High-level helper thunk used for partial config updates.
 *
 * Responsibilities:
 *   • merges partial config updates
 *   • persists changes to backend
 *   • updates Redux state
 *   • updates LocalStorage
 *
 *
 * ============================================================================
 * COLUMN MANAGEMENT
 * ============================================================================
 *
 * columnsByPage
 * -------------
 * Stores table configuration separately for each page.
 *
 * Includes:
 *   • column order
 *   • visibility
 *   • pinning
 *   • locked state
 *
 *
 * LOCKED COLUMNS
 * --------------
 * Certain columns are always protected from reordering/removal:
 *
 *   • Geo columns
 *   • Device columns
 *   • Payment type column
 *   • Currency column (CCY)
 *
 *
 * PINNING RULES
 * -------------
 * Geo columns support grouped pinning behavior.
 *
 * Features:
 *   • sequential geo pinning
 *   • automatic geo reset
 *   • partial geo pin toggling
 *   • preserved pin state
 *
 *
 * COLUMN VISIBILITY
 * -----------------
 * Visibility is dynamically controlled for:
 *
 *   • Device column
 *   • Device type column
 *   • Payment type column
 *
 * based on runtime flags received from the UI layer.
 *
 *
 * ============================================================================
 * THEME HANDLING
 * ============================================================================
 *
 * Theme updates require a side effect:
 *
 *      applyTheme(theme)
 *
 * because themes directly modify:
 *
 *   • HTML classes
 *   • CSS variables
 *   • visual application styling
 *
 *
 * Theme is applied when:
 *
 *   • user manually changes theme
 *   • backend config is loaded
 *   • platform-specific theme is detected
 *
 *
 * PLATFORM THEMES
 * ---------------
 * themesByPlatform allows different themes depending on platform type.
 *
 * Example:
 *   • desktop → dark
 *   • mobile  → light
 *
 *
 * ============================================================================
 * LOCAL STORAGE
 * ============================================================================
 *
 * LocalStorage is used as:
 *
 *   • persistence fallback
 *   • startup cache
 *   • offline-safe backup
 *
 * Helper methods:
 *
 *   • getConfigFromLocalStorage()
 *   • setConfigToLocalStorage()
 *
 *
 * ============================================================================
 * MOBILE SUPPORT
 * ============================================================================
 *
 * Mobile mode is automatically detected using:
 *
 *   • screen width
 *   • responsive helpers
 *
 * Used for:
 *
 *   • responsive layouts
 *   • adaptive table behavior
 *   • sidebar handling
 *
 *
 * ============================================================================
 * IMPORTANT NOTES
 * ============================================================================
 *
 *   • Redux state must remain serializable.
 *   • Backend config should always be treated as source of truth.
 *   • LocalStorage updates should mirror backend updates.
 *   • Theme side effects must never be skipped.
 *   • Column ordering must preserve locked column constraints.
 *
 * ============================================================================
 */

interface Config {
  isThumbnailView: boolean;
  graphTimeMinutesStep: number;
  selectedRowDensity: RowDensity;
  isMobile: boolean;
  isSidebarMinimized: boolean;
  activeSidebarElement: SidebarMenu;
  rowDensity?: RowDensity;
  timeZone: string;
  theme: ThemeEnum;
  themesByPlatform: PlatformType[];
  format: keyof typeof NUMBER_FORMAT_LOCALES;
  username: string;
  table: {
    range: {
      from: number;
      to: number;
      total: number;
    };
  };
  columnsByPage: Record<string, Column[]>;
  activePageKey: string;
  selectedCurrency: string | null;
}

const initialState: Config = {
  isThumbnailView: false,
  graphTimeMinutesStep: 15,
  selectedRowDensity: getConfigFromLocalStorage()?.rowDensity || RowDensity.NORMAL,
  isMobile: isMobileScreen(getWindowWidth()),
  isSidebarMinimized: false,
  activeSidebarElement: SidebarMenu.NONE,
  rowDensity: getConfigFromLocalStorage()?.rowDensity || RowDensity.NORMAL,
  timeZone:
    getConfigFromLocalStorage()?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE,
  theme: getConfigFromLocalStorage()?.theme || ThemeEnum.LIGHT,
  themesByPlatform: getConfigFromLocalStorage()?.themesByPlatform || [],
  format: getConfigFromLocalStorage()?.format || (US as keyof typeof NUMBER_FORMAT_LOCALES),
  username: getConfigFromLocalStorage()?.username || '',
  table: {
    range: {
      from: 0,
      to: 0,
      total: 0,
    },
  },
  columnsByPage: getConfigFromLocalStorage()?.columnsByPage || {},
  activePageKey: '',
  selectedCurrency: getConfigFromLocalStorage()?.selectedCurrency || null,
};

export const getConfig = createAsyncThunk('config/getConfig', async () => {
  const response = await ApiConsumer.getConfig();

  return response;
});
export const updateConfig = createAsyncThunk('config/updateConfig', async (config: ApplicationConfig) => {
  const response = await ApiConsumer.updateConfig(config);

  return response;
});

export const saveConfigToBackend = createAsyncThunk<
  Partial<ApplicationConfig>,
  Partial<ApplicationConfig>,
  { dispatch: AppDispatch }
>('config/saveConfigToBackend', async (newConfig, { dispatch }) => {
  await updateApplicationConfig(newConfig, dispatch);
  return newConfig;
});

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setIsThumbnailView: (state) => {
      state.isThumbnailView = !state.isThumbnailView;
    },
    setSelectedRowDensity: (state, { payload }) => {
      state.selectedRowDensity = payload;
    },
    setSelectedTimeZone: (state, { payload }) => {
      state.timeZone = payload;
    },
    setTheme(state, { payload }) {
      state.theme = payload;
      applyTheme(payload);
    },
    setIsMobile(state, { payload }) {
      state.isMobile = payload;
    },
    setIsSidebarMinimized(state) {
      state.isSidebarMinimized = !state.isSidebarMinimized;
    },
    setActiveSidebarElement(state, { payload }) {
      state.activeSidebarElement = payload;
    },
    setTableRange: (state, action) => {
      state.table.range = action.payload;
    },
    toggleFormat: (state) => {
      state.format = state.format === US ? EU : US;
    },
    setUserName: (state, { payload }) => {
      state.username = payload;
    },
    setActivePageKey(state, action: PayloadAction<string>) {
      state.activePageKey = action.payload;
    },
    setColumns(
      state,
      action: PayloadAction<{
        columns: string[];
        pageKey: string;
        showDevice: boolean;
        showDeviceType: boolean;
        showPaymentType: boolean;
      }>,
    ) {
      const geoLocked = Object.values(GeoColumns) as string[];
      const deviceColumns = Object.values(DeviceColumns) as string[];
      const paymentTypeColumn = Object.values(PaymentTypeColumn) as string[];
      const lockedColumns = new Set<string>([
        ...geoLocked,
        ...deviceColumns,
        ...paymentTypeColumn,
        StatsTableColumn.CCY,
      ]);
      const existingColumns = state.columnsByPage[action.payload.pageKey] || [];
      const existingMap = new Map(existingColumns.map((c) => [c.name, c]));
      const incomingColumns = action.payload.columns.filter((col) => !HIDDEN_COLUMNS.includes(col));
      const geoColumns = incomingColumns.filter((c) => isGeoColumn(c));
      const filterDependentColumns = incomingColumns.filter((c) => isFilterDependentColumn(c) && !isGeoColumn(c));
      const userColumnsIncoming = incomingColumns.filter(
        (c) => !isGeoColumn(c) && !isFilterDependentColumn(c) && c !== StatsTableColumn.CCY,
      );
      const persistedUserColumns = existingColumns.filter((c) => userColumnsIncoming.includes(c.name));
      const newUserColumns = userColumnsIncoming
        .filter((name) => !persistedUserColumns.some((c) => c.name === name))
        .map((name) => ({
          name,
          isVisible: true,
          isLocked: false,
          isPinned: false,
        }));

      const finalOrder = [
        ...geoColumns,
        ...filterDependentColumns,
        StatsTableColumn.CCY,

        ...persistedUserColumns.map((c) => c.name),
        ...newUserColumns.map((c) => c.name),
      ];

      const updatedColumns: Column[] = finalOrder
        .filter((col) => !HIDDEN_COLUMNS.includes(col))
        .map((col) => {
          const existing = existingMap.get(col);
          const isDevice = col === DeviceColumns.DEVICE;
          const isDeviceType = col === DeviceColumns.DEVICE_TYPE_ENUM;
          const isPaymentType = col === PaymentTypeColumn.PAYMENT_TYPE;

          let isVisible =
            existing?.isVisible ??
            (isGeoColumn(col)
              ? DEFAULT_VISIBLE_GEO_COLUMNS.includes(col as GeoColumns)
              : !DEFAULT_UNVISIBLE_COLUMNS.includes(col as StatsTableColumn));

          if (isDevice) {
            isVisible = action.payload.showDevice;
          }

          if (isDeviceType) {
            isVisible = action.payload.showDeviceType;
          }

          if (isPaymentType) {
            isVisible = action.payload.showPaymentType;
          }

          return {
            name: col,
            isVisible,
            isLocked: lockedColumns.has(col),
            isPinned: existing?.isPinned ?? isGeoColumn(col),
          };
        });

      state.columnsByPage[action.payload.pageKey] = updatedColumns;
    },

    setPinnedFromIndex(
      state,
      action: PayloadAction<{
        pageKey: string;
        columnName: string;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];
      const geoColumns = columns.filter((c) => isGeoColumn(c.name));

      const clickedIndex = geoColumns.findIndex((col) => col.name === action.payload.columnName);

      if (clickedIndex === -1) return;

      const clickedCol = geoColumns[clickedIndex];

      geoColumns.forEach((geoCol, index) => {
        const realColumn = columns.find((c) => c.name === geoCol.name);

        if (!realColumn) return;

        if (clickedCol.isPinned) {
          realColumn.isPinned = index < clickedIndex;
        } else {
          realColumn.isPinned = index <= clickedIndex;
        }
      });
    },

    togglePin(
      state,
      action: PayloadAction<{
        pageKey: string;
        columnName: string;
      }>,
    ) {
      const { pageKey, columnName } = action.payload;

      if (!state.columnsByPage[pageKey]) {
        state.columnsByPage[pageKey] = [];
      }

      const column = state.columnsByPage[pageKey].find((c) => c.name === columnName);

      if (column) {
        column.isPinned = !column.isPinned;
      }
    },

    resetGeoPins(
      state,
      action: PayloadAction<{
        pageKey: string;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];

      columns.forEach((col) => {
        if (isGeoColumn(col.name)) {
          col.isPinned = false;
        }
      });
    },

    setGeoPinsTrue(
      state,
      action: PayloadAction<{
        pageKey: string;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];

      columns.forEach((col) => {
        if (isGeoColumn(col.name)) {
          col.isPinned = true;
        }
      });
    },

    setVisible(
      state,
      action: PayloadAction<{
        pageKey: string;
        columnName: string;
        visible: boolean;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];

      const column = columns.find((c) => c.name === action.payload.columnName);

      if (column) {
        column.isVisible = action.payload.visible;
      }
    },

    toggleVisible(
      state,
      action: PayloadAction<{
        pageKey: string;
        columnName: string;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];

      const column = columns.find((c) => c.name === action.payload.columnName);

      if (column) {
        column.isVisible = !column.isVisible;
      }
    },

    moveColumn(
      state,
      action: PayloadAction<{
        pageKey: string;
        from: number;
        to: number;
      }>,
    ) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];

      const { from, to } = action.payload;

      const geoColumns = columns.filter((c) => isGeoColumn(c.name));
      const runtimeColumns = columns.filter((c: Column) => isFilterDependentColumn(c.name) && !isGeoColumn(c.name));
      const ccy = columns.find((c) => c.name === StatsTableColumn.CCY);

      // USER MOVABLE COLUMNS
      const movable = columns.filter((c) => isUserColumn(c.name));
      const pinned = movable.filter((c) => c.isPinned);
      const unpinned = movable.filter((c) => !c.isPinned);

      // DETERMINE DRAGGED COLUMN
      const dragged = movable[from];

      if (!dragged) return;

      // MOVE INSIDE PINNED
      if (dragged.isPinned) {
        if (to < 0 || to >= pinned.length) return;

        const updatedPinned = moveItem(pinned, from, to);

        state.columnsByPage[action.payload.pageKey] = [
          ...geoColumns,
          ...runtimeColumns,
          ...(ccy ? [ccy] : []),
          ...updatedPinned,
          ...unpinned,
        ];

        return;
      }

      // MOVE INSIDE UNPINNED
      const unpinnedFrom = from - pinned.length;
      const unpinnedTo = to - pinned.length;

      if (unpinnedFrom < 0 || unpinnedFrom >= unpinned.length) {
        return;
      }

      if (unpinnedTo < 0 || unpinnedTo >= unpinned.length) {
        return;
      }

      const updatedUnpinned = moveItem(unpinned, unpinnedFrom, unpinnedTo);

      state.columnsByPage[action.payload.pageKey] = [
        ...geoColumns,
        ...runtimeColumns,
        ...(ccy ? [ccy] : []),
        ...pinned,
        ...updatedUnpinned,
      ];
    },

    /**
     * Sets a TEMPORARY visibility flag for a single geo column,
     * without touching the persisted isVisible value.
     *
     * Used by the Sum By dropdown checkboxes - clicking a checkbox
     * only updates pendingVisible, so the table and the backend
     * config stay untouched until the user confirms the change
     * (via Sum By / Search / Update data -> commitPendingVisible).
     */
    setPendingVisible(state, action: PayloadAction<{ pageKey: string; columnName: string; visible: boolean }>) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];
      const column = columns.find((c) => c.name === action.payload.columnName);
      if (column) {
        column.pendingVisible = action.payload.visible;
      }
    },

    /**
     * Applies all pending visibility changes for a page: copies
     * pendingVisible into isVisible (the value that actually
     * controls table rendering and gets persisted to the backend),
     * then clears pendingVisible.
     *
     * Called once, at the start of handleApplyAllFilters, so every
     * confirm action (Sum By / Search / Update data) commits the
     * user's checkbox choices before groupBy/fetch/persist run.
     */
    commitPendingVisible(state, action: PayloadAction<{ pageKey: string }>) {
      const columns = state.columnsByPage[action.payload.pageKey] || [];
      columns.forEach((col) => {
        if (col.pendingVisible !== undefined) {
          col.isVisible = col.pendingVisible;
          col.pendingVisible = undefined;
        }
      });
    },
    setSelectedCurrency: (state, { payload }) => {
      state.selectedCurrency = payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getConfig.fulfilled, (state, { payload }) => {
      if (!getItemFromLocalStorage(StorageKey.ACCESS_TOKEN)) {
        return;
      }
      state.rowDensity = payload.Config.rowDensity ?? RowDensity.NORMAL;
      state.selectedRowDensity = payload.Config.rowDensity ?? RowDensity.NORMAL;
      state.timeZone = payload.Config.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIMEZONE;

      state.themesByPlatform = payload.Config.themesByPlatform ?? [];
      state.format = payload.Config.format ?? (US as keyof typeof NUMBER_FORMAT_LOCALES);
      state.username = payload.Config.username ?? state.username;
      state.selectedCurrency = payload.Config.selectedCurrency ?? state.selectedCurrency;

      Object.entries(payload.Config.columnsByPage || {}).forEach(([pageKey, backendColumns]) => {
        const existing = state.columnsByPage[pageKey] || [];
        const runtimeColumns = existing.filter((c) => isFilterDependentColumn(c.name));
        const ccyColumn = backendColumns.find((c) => isCurrencyColumn(c.name));
        const userColumns = backendColumns.filter((c) => isUserColumn(c.name));
        const geoColumns = backendColumns.filter((c) => isGeoColumn(c.name));

        if (pageKey === CASH_REGISTER_PAGE) {
          return;
        }

        state.columnsByPage[pageKey] = [
          ...geoColumns,
          ...runtimeColumns,
          ...(ccyColumn ? [ccyColumn] : []),
          ...userColumns,
        ];
      });
      setConfigToLocalStorage(payload.Config);
    });

    builder.addCase(getConfig.rejected, (state, { payload }) => {
      state.rowDensity = getConfigFromLocalStorage()?.rowDensity;
    });

    builder.addCase(updateConfig.fulfilled, (state, { payload }) => {
      if (payload.ResponseCode === AgmsResponseCode.OK) {
        setConfigToLocalStorage(payload.Config);
        state.rowDensity = payload.Config.rowDensity ?? state.rowDensity;
        state.selectedRowDensity = payload.Config.rowDensity ?? state.selectedRowDensity;
        state.timeZone = payload.Config.timeZone ?? state.timeZone;
        state.themesByPlatform = payload.Config.themesByPlatform ?? state.themesByPlatform;
        state.format = payload.Config.format ?? state.format;
        state.username = payload.Config.username ?? state.username;
        state.selectedCurrency = payload.Config.selectedCurrency ?? state.selectedCurrency;

        Object.entries(payload.Config.columnsByPage || {}).forEach(([pageKey, backendColumns]) => {
          const existing = state.columnsByPage[pageKey] || [];
          const runtimeColumns = existing.filter((c) => isFilterDependentColumn(c.name));
          const ccyColumn = backendColumns.find((c) => isCurrencyColumn(c.name));
          const userColumns = backendColumns.filter((c) => isUserColumn(c.name));
          const geoColumns = backendColumns.filter((c) => isGeoColumn(c.name));

          if (pageKey === CASH_REGISTER_PAGE) {
            return;
          }

          state.columnsByPage[pageKey] = [
            ...geoColumns,
            ...runtimeColumns,
            ...(ccyColumn ? [ccyColumn] : []),
            ...userColumns,
          ];
        });
        setConfigToLocalStorage(payload.Config);
        if (state.themesByPlatform) {
          const platformTheme = getThemeFromPlatform(payload.Config.themesByPlatform || []);
          applyTheme(platformTheme || state.theme);
        } else if (payload.Config.theme) {
          applyTheme(payload.Config.theme);
        } else {
          applyTheme(state.theme);
        }
      }
    });
  },
});

export const {
  setIsThumbnailView,
  setSelectedRowDensity,
  setSelectedTimeZone,
  setTheme,
  setIsMobile,
  setIsSidebarMinimized,
  setActiveSidebarElement,
  setTableRange,
  toggleFormat,
  setUserName,
  setColumns,
  togglePin,
  toggleVisible,
  moveColumn,
  setVisible,
  resetGeoPins,
  setGeoPinsTrue,
  setPinnedFromIndex,
  setActivePageKey,
  commitPendingVisible,
  setPendingVisible,
  setSelectedCurrency,
} = configSlice.actions;

export default configSlice.reducer;
